import { xeroLogoBlack } from '$assets'
import {
  AU_Region,
  ClassifierType,
  Country,
  LogicalOperatorType,
  PayrollSystem,
  UnavailabilityRecordType,
} from './enums'
import {
  PayrollEmployee,
  PayrollSystemProps,
  StaffMember,
  StaffMemberNz,
  StaffMemberXeroAu,
  XeroAuEmployee,
  XeroNzEmployee,
} from '$models'
import { getDateStringWithoutTimeZone } from 'src/components/common/helpers'
import dayjs from 'dayjs'
import {
  formatBankAccountNumber,
  formatBSB,
  formatIRD,
  buildStatementText,
  formatTaxFileNumber,
  getActiveCompanySettings,
  stringArrayToHumanReadableList,
} from '$utils'
import { baseXeroEmployeeValidationMessage } from './strings'

export const variationOperatorMapping: Record<
  ClassifierType,
  LogicalOperatorType[]
> = {
  [ClassifierType.Employment]: [
    LogicalOperatorType.Equals,
    LogicalOperatorType.DoesNotEqual,
  ],
  [ClassifierType.Age]: [
    LogicalOperatorType.Equals,
    LogicalOperatorType.DoesNotEqual,
    LogicalOperatorType.IsGreaterThan,
    LogicalOperatorType.IsLessThan,
  ],
  [ClassifierType.Remuneration]: [
    LogicalOperatorType.Equals,
    LogicalOperatorType.DoesNotEqual,
  ],
  [ClassifierType.ContractedWeeklyHours]: [
    LogicalOperatorType.Equals,
    LogicalOperatorType.DoesNotEqual,
    LogicalOperatorType.IsGreaterThan,
    LogicalOperatorType.IsLessThan,
  ],
  [ClassifierType.StartDate]: [
    LogicalOperatorType.IsOnOrAfter,
    LogicalOperatorType.IsOnOrBefore,
  ],
}

/**
 * We can't have types as values, that is why this not integrated in the payrollSystemMapping
 * This mapping is needed to know what PayrollSystemEmployee object needs to be used depending on the PayrollSystem
 */
export type EmployeeTypeMap = {
  [PayrollSystem.XeroNz]: XeroNzEmployee
  [PayrollSystem.XeroAu]: XeroAuEmployee
  [PayrollSystem.Custom]: undefined
  [PayrollSystem.AcePayroll]: undefined
  [PayrollSystem.AffinityPayroll]: undefined
  [PayrollSystem.CrystalPayroll]: undefined
  [PayrollSystem.DataPay]: undefined
  [PayrollSystem.EzyPay]: undefined
  [PayrollSystem.Flexitime]: undefined
  [PayrollSystem.IPayroll]: undefined
  [PayrollSystem.MyobExo]: undefined
  [PayrollSystem.MyobPayroll]: undefined
  [PayrollSystem.PayHero]: undefined
  [PayrollSystem.PaySauce]: undefined
  [PayrollSystem.Smartly]: undefined
}

/**
 * @property name PayrollSystem name to be used for the customer
 * @property shortName PayrollSystem short name
 * @property logo PayrollSystem logo to be used for the customer
 * @property getConnectionStatusApi returns the api path to check the connection status against the payroll system
 * @property getPayRunUrl returns the pay run url for the PayrollSystem //Being {0} the CompanyId and {1} the PayRunId
 * @property template The keys of this object are the section names and each section has an array of {name, fieldsToCompare}
 * where name is used for knowing what field(s) to render and fieldsToCompare is to know
 * what fields needs to be copied from one side to the other
 * @property transformToForm is the mapping needed to map the PayrollSystem employee object to a Partial<StaffMember> object in order to be able to copy things across
 * @property transformFromForm is the mapping needed to map the Partial<StaffMember> object to a PayrollSystem employee object in order to be able to save it
 * @property transformOnSave transforms the PayrollEmployee into the format to be sent to the SAVE API
 * @property transformOnGet transforms the GET API result into a PayrollEmployee format to be used within the solution
 */
export const payrollSystemMapping: Record<
  PayrollSystem | 'default',
  PayrollSystemProps
> = {
  default: {
    name: 'default',
    template: () => {
      return {
        ['Personal Details']: {
          fields: [
            {
              name: 'payCode',
              fieldsToCompare: ['payCode'],
            },
          ],
        },
      }
    },
  },
  [PayrollSystem.XeroAu]: {
    name: 'Xero Australia',
    shortName: 'Xero',
    logo: xeroLogoBlack,
    country: Country.Australia,
    getConnectionStatusApi: (module?: string) =>
      `/connections/xero/status?module=${module}`,
    getPayRunUrl: () => 'https://www.xero.com/au',
    template: (watch) => {
      return {
        ['Personal Details']: {
          fields: [
            {
              name: 'firstName',
              fieldsToCompare: ['firstName'],
            },
            {
              name: 'lastName',
              fieldsToCompare: ['lastName'],
            },
            {
              name: 'dob',
              fieldsToCompare: ['dob'],
            },
            {
              name: 'gender',
              fieldsToCompare: ['gender'],
            },
            {
              name: 'streetCityZipCode',
              fieldsToCompare: [
                'homeAddressStreet',
                'homeAddressCity',
                'homeAddressState',
                'homeAddressZipCode',
              ],
            },
          ],
        },
        ['Contact Details']: {
          fields: [
            {
              name: 'email',
              fieldsToCompare: ['email'],
            },
            {
              name: 'phoneMobile',
              fieldsToCompare: ['phoneMobile'],
            },
          ],
        },
        ['Job Details']: {
          fields: [
            {
              name: 'startDate',
              fieldsToCompare: ['datestampStarted'],
            },
          ],
        },
        ['Employment Schedule']: {
          fields: [
            {
              name: 'employmentType',
              fieldsToCompare: ['employmentType'],
              disableLeftCopy: true,
            },
          ],
        },
        ['Payroll']: {
          isPayrollRelated: true,
          fields: [
            {
              name: 'calendarId',
              fieldsToCompare: ['calendarId'],
            },
            /*
            The "ordinaryEarningsRateId" was stored as a decimal but should be saved as a string 
            since it represents an ID from an external system (e.g., XERO). For more context, refer to
            https://loaded-hub.atlassian.net/browse/LOADED-5518        
            */
            // {
            //   name: 'ordinaryEarningsRateId',
            //   fieldsToCompare: ['ordinaryEarningsRateId'],
            // },
          ],
          fieldsToKeepWhenRequestEmployeeInformation: [
            'calendarId',
            // 'ordinaryEarningsRateId',
          ],
        },
        ['Bank Details']: {
          isPayrollRelated: true,
          fields: [
            {
              name: 'bankDetailsSection',
              fieldsToCompare: ['bankAccountName', 'bankAccountNumber', 'bsb'],
              disableRightCopy: watch
                ? !watch('staffMember.bankAccountName') ||
                  !watch('staffMember.bankAccountNumber') ||
                  !watch('staffMember.bsb')
                : false,
            },
          ],
        },
        ['Tax Declaration']: {
          isPayrollRelated: true,
          fields: [
            {
              name: 'taxFileNumber',
              fieldsToCompare: ['taxFileNumber', 'hasTaxFileNumberExemption'],
              disableLeftCopy: true,
            },
            {
              name: 'taxFileNumberExemptionType',
              fieldsToCompare: [
                'taxFileNumberExemptionType',
                'hasTaxFileNumberExemption',
              ],
            },
            {
              name: 'residencyStatus',
              fieldsToCompare: ['residencyStatus'],
            },
            {
              name: 'incomeType',
              fieldsToCompare: ['incomeType'],
            },
            {
              name: 'taxScaleType',
              fieldsToCompare: ['taxScaleType'],
            },
            {
              name: 'payeesWorkingCondition',
              fieldsToCompare: ['payeesWorkingCondition'],
            },
            {
              name: 'pensionersMaritalStatus',
              fieldsToCompare: ['pensionersMaritalStatus'],
            },
            {
              name: 'hasStudyLoanOrFinancialDebt',
              fieldsToCompare: ['hasStudyLoanOrFinancialDebt'],
            },
            {
              name: 'claimingTaxFreeThreshold',
              fieldsToCompare: ['claimingTaxFreeThreshold'],
            },
            {
              name: 'claimingZoneOverseasOrInvalid',
              fieldsToCompare: ['claimingZoneOverseasOrInvalid'],
            },
            {
              name: 'annualOffsetAmountClaimed',
              fieldsToCompare: ['annualOffsetAmountClaimed'],
            },
            {
              name: 'increaseTaxWithholding',
              fieldsToCompare: ['increaseTaxWithholding'],
            },
            {
              name: 'taxWithholdingAmount',
              fieldsToCompare: ['taxWithholdingAmount'],
            },
          ],
        },
        ['Superannuation']: {
          isPayrollRelated: true,
          fields: [
            {
              name: 'superFundDetailsSection',
              fieldsToCompare: ['superFundNameOrUsi', 'superFundMemberNumber'],
              disableRightCopy: watch
                ? !watch('staffMember.superFundNameOrUsi') ||
                  !watch('staffMember.superFundMemberNumber')
                : false,
            },
          ],
        },
      }
    },
    requestInformationTemplate: {
      ['Basic Information']: {
        fields: [
          {
            name: 'basicInformationSection',
            fieldsToCompare: [],
          },
        ],
      },
      ['Contact Details']: {
        fields: [
          {
            name: 'contactDetailsSection',
            fieldsToCompare: [],
          },
        ],
      },
      ['Emergency Contact']: {
        fields: [
          {
            name: 'emergencyContactSection',
            fieldsToCompare: [],
          },
        ],
      },
      ['Bank Details']: {
        isPayrollRelated: true,
        fields: [
          {
            name: 'bankAccountName',
            fieldsToCompare: [],
          },
          {
            name: 'bankAccountNumber',
            fieldsToCompare: [],
          },
          {
            name: 'bsb',
            fieldsToCompare: [],
          },
        ],
      },
      ['Fair Work Information Statement']: {
        isPayrollRelated: true,
        fields: [
          {
            name: 'providedWithFairWorkInformationStatement',
            fieldsToCompare: [],
          },
        ],
      },
      ['Tax Declaration']: {
        isPayrollRelated: true,
        fields: [
          {
            name: 'hasChangedNameSinceLastATO',
            fieldsToCompare: [],
          },
          {
            name: 'taxFileNumber',
            fieldsToCompare: [],
          },
          {
            name: 'taxFileNumberExemptionType',
            fieldsToCompare: [],
          },
          {
            name: 'residencyStatus',
            fieldsToCompare: [],
          },
          {
            name: 'incomeType',
            fieldsToCompare: [],
          },
          {
            name: 'taxScaleType',
            fieldsToCompare: [],
          },
          {
            name: 'pensionersMaritalStatus',
            fieldsToCompare: [],
          },
          {
            name: 'claimingTaxFreeThreshold',
            fieldsToCompare: [],
          },
          {
            name: 'claimingZoneOverseasOrInvalid',
            fieldsToCompare: [],
          },
          {
            name: 'hasStudyLoan',
            fieldsToCompare: [],
          },
          {
            name: 'hasFinancialDebt',
            fieldsToCompare: [],
          },
        ],
      },
      ['Superannuation']: {
        isPayrollRelated: true,
        fields: [
          {
            name: 'superFundId',
            fieldsToCompare: [],
          },
          {
            name: 'superFundSelfManaged',
            fieldsToCompare: [],
          },
          {
            name: 'superFundNameOrUsi',
            fieldsToCompare: [],
          },
          {
            name: 'superFundMemberNumber',
            fieldsToCompare: [],
          },
          {
            name: 'superFundABN',
            fieldsToCompare: [],
          },
          {
            name: 'superFundESA',
            fieldsToCompare: [],
          },
          {
            name: 'superFundAccountName',
            fieldsToCompare: [],
          },
          {
            name: 'superFundAccountNumber',
            fieldsToCompare: [],
          },
          {
            name: 'superFundBSB',
            fieldsToCompare: [],
          },
        ],
      },
    },
    transformToForm: (
      payrollEmployee: PayrollEmployee
    ): Partial<StaffMember> => {
      const transformedPayrollEmployee = payrollEmployee as XeroAuEmployee
      return {
        ...transformedPayrollEmployee,
        dob: transformedPayrollEmployee.dateOfBirth,
        gender: transformedPayrollEmployee.gender?.toLocaleLowerCase(),
        phoneMobile: transformedPayrollEmployee.phoneNumber,
        datestampStarted: transformedPayrollEmployee.startDate,
        homeAddressStreet: transformedPayrollEmployee.addressLine1,
        homeAddressCity: transformedPayrollEmployee.city,
        homeAddressZipCode: transformedPayrollEmployee.postCode,
        homeAddressCountry: transformedPayrollEmployee.country,
        homeAddressState: transformedPayrollEmployee.region,
      }
    },
    transformFromForm(
      partialStaffMember: Partial<StaffMemberXeroAu>
    ): XeroAuEmployee {
      return {
        id: partialStaffMember.id!,
        firstName: partialStaffMember.firstName!,
        lastName: partialStaffMember.lastName,
        dateOfBirth: partialStaffMember.dob,
        startDate: partialStaffMember.datestampStarted,
        gender: partialStaffMember.gender,
        email: partialStaffMember.email,
        phoneNumber: partialStaffMember.phoneMobile,
        addressLine1: partialStaffMember.homeAddressStreet,
        city: partialStaffMember.homeAddressCity,
        postCode: partialStaffMember.homeAddressZipCode,
        country: partialStaffMember.homeAddressCountry,
        region: partialStaffMember.homeAddressState,
        bankAccountName: partialStaffMember.bankAccountName,
        bankAccountNumber: partialStaffMember.bankAccountNumber,
        bsb: partialStaffMember.bsb,
        taxFileNumber: partialStaffMember.taxFileNumber,
        hasTaxFileNumberExemption: partialStaffMember.hasTaxFileNumberExemption,
        taxFileNumberExemptionType:
          partialStaffMember.taxFileNumberExemptionType,
        residencyStatus: partialStaffMember.residencyStatus,
        incomeType: partialStaffMember.incomeType,
        taxScaleType: partialStaffMember.taxScaleType,
        claimingTaxFreeThreshold: partialStaffMember.claimingTaxFreeThreshold,
        claimingZoneOverseasOrInvalid:
          partialStaffMember.claimingZoneOverseasOrInvalid,
        annualOffsetAmountClaimed: partialStaffMember.annualOffsetAmountClaimed,
        payeesWorkingCondition: partialStaffMember.payeesWorkingCondition,
        hasStudyLoanOrFinancialDebt:
          partialStaffMember.hasStudyLoanOrFinancialDebt,
        pensionersMaritalStatus: partialStaffMember.pensionersMaritalStatus,
        superFundId: partialStaffMember.superFundId,
        superMembershipNumber: partialStaffMember.superMembershipNumber,
        notEligibleForSuperFund: partialStaffMember.notEligibleForSuperFund,
        superFundNameOrUsi: partialStaffMember.superFundNameOrUsi,
        superFundSelfManaged: partialStaffMember.superFundSelfManaged,
        superFundMemberNumber: partialStaffMember.superFundMemberNumber,
        superFundABN: partialStaffMember.superFundABN,
        superFundESA: partialStaffMember.superFundESA,
        superFundAccountName: partialStaffMember.superFundAccountName,
        superFundAccountNumber: partialStaffMember.superFundAccountNumber,
        superFundBSB: partialStaffMember.superFundBSB,
        calendarId: partialStaffMember.calendarId,
        ordinaryEarningsRateId: partialStaffMember.ordinaryEarningsRateId,
        employmentType: partialStaffMember.employmentType,
        increaseTaxWithholding: partialStaffMember.increaseTaxWithholding,
        taxWithholdingAmount: partialStaffMember.taxWithholdingAmount,
      }
    },
    transformOnSave: (employee: PayrollEmployee) => {
      const auEmployee = employee as XeroAuEmployee
      const activeCompany = getActiveCompanySettings()
      return {
        ...auEmployee,
        dateOfBirth:
          auEmployee.dateOfBirth == null
            ? auEmployee.dateOfBirth
            : getDateStringWithoutTimeZone(auEmployee.dateOfBirth),
        gender: auEmployee.gender ? auEmployee.gender : null,
        startDate:
          auEmployee.startDate == null
            ? auEmployee.startDate
            : getDateStringWithoutTimeZone(auEmployee.startDate),
        bankAccountNumber: auEmployee.bankAccountNumber?.replaceAll('-', ''),
        bsb: auEmployee.bsb?.replaceAll('-', ''),
        superFundBSB: auEmployee.superFundBSB?.replaceAll('-', ''),
        taxFileNumber: auEmployee.taxFileNumber?.replaceAll('-', ''),
        statementText: buildStatementText(activeCompany.name),
      }
    },
    transformOnGet: (employee: PayrollEmployee) => {
      const auEmployee = employee as XeroAuEmployee
      return {
        ...auEmployee,
        dateOfBirth:
          auEmployee.dateOfBirth == null
            ? auEmployee.dateOfBirth
            : dayjs.utc(auEmployee.dateOfBirth),
        gender: auEmployee.gender ? auEmployee.gender : null,
        startDate:
          auEmployee.startDate == null
            ? auEmployee.startDate
            : dayjs.utc(auEmployee.startDate),
        bankAccountNumber: formatBankAccountNumber(
          auEmployee.bankAccountNumber,
          Country.Australia
        ),
        bsb: formatBSB(auEmployee.bsb),
        superFundBSB: formatBSB(auEmployee.superFundBSB),
        taxFileNumber: formatTaxFileNumber(auEmployee.taxFileNumber),
      }
    },
    validateOnCreate: (staffMember: Partial<StaffMember>) => {
      const missing: Array<{ label: string; fieldName: string }> = []

      if (staffMember.firstName == null || staffMember.firstName === '') {
        missing.push({ label: 'a first name', fieldName: 'firstName' })
      }

      if (staffMember.lastName == null || staffMember.lastName === '') {
        missing.push({ label: 'a last name', fieldName: 'lastName' })
      }

      if (staffMember.dob == null) {
        missing.push({ label: 'a birth date', fieldName: 'dob' })
      }

      if (
        staffMember.homeAddressStreet == undefined ||
        staffMember.homeAddressStreet === ''
      ) {
        missing.push({
          label: 'a street address',
          fieldName: 'homeAddressStreet',
        })
      }

      if (
        staffMember.homeAddressCity == undefined ||
        staffMember.homeAddressCity === ''
      ) {
        missing.push({ label: 'a city', fieldName: 'homeAddressCity' })
      }

      if (
        staffMember.homeAddressZipCode == undefined ||
        staffMember.homeAddressZipCode === ''
      ) {
        missing.push({ label: 'a postcode', fieldName: 'homeAddressZipCode' })
      }

      if (
        staffMember.homeAddressState == undefined ||
        !(staffMember.homeAddressState in AU_Region)
      ) {
        missing.push({
          label: 'an Australian state',
          fieldName: 'homeAddressState',
        })
      }

      if (missing.length === 0) {
        return null
      }

      const fieldsList = stringArrayToHumanReadableList(
        missing.map((m) => m.label)
      )

      return {
        message: `${baseXeroEmployeeValidationMessage}${fieldsList}`,
        fieldNames: new Set(missing.map((m) => m.fieldName)),
      }
    },
  },
  [PayrollSystem.XeroNz]: {
    name: 'Xero New Zealand',
    shortName: 'Xero',
    logo: xeroLogoBlack,
    getConnectionStatusApi: (module?: string) =>
      `/connections/xero/status?module=${module}`,
    getPayRunUrl: () => 'https://www.xero.com/nz',
    template: (watch) => {
      return {
        ['Personal Details']: {
          fields: [
            {
              name: 'firstName',
              fieldsToCompare: ['firstName'],
            },
            {
              name: 'lastName',
              fieldsToCompare: ['lastName'],
            },
            {
              name: 'dob',
              fieldsToCompare: ['dob'],
            },
            {
              name: 'streetCityZipCode',
              fieldsToCompare: [
                'homeAddressStreet',
                'homeAddressCity',
                'homeAddressZipCode',
              ],
            },
          ],
        },
        ['Job Details']: {
          fields: [
            {
              name: 'startDate',
              fieldsToCompare: ['datestampStarted'],
            },
          ],
        },
        ['Bank Details']: {
          isPayrollRelated: true,
          fields: [
            {
              name: 'bankDetailsSection',
              fieldsToCompare: ['bankAccountName', 'bankAccountNumber'],
              disableRightCopy: watch
                ? !watch('staffMember.bankAccountName') ||
                  !watch('staffMember.bankAccountNumber')
                : false,
            },
          ],
        },
        ['Tax Details']: {
          isPayrollRelated: true,
          fields: [
            {
              name: 'irdNumber',
              fieldsToCompare: ['irdNumber'],
            },
            {
              name: 'taxCode',
              fieldsToCompare: ['taxCode'],
            },
            {
              name: 'employerKiwisaverContributionTaxRate',
              fieldsToCompare: ['employerKiwisaverContributionTaxRate'],
            },
          ],
          fieldsToKeepWhenRequestEmployeeInformation: [
            'employerKiwisaverContributionTaxRate',
          ],
        },
        ['KiwiSaver']: {
          isPayrollRelated: true,
          fields: [
            {
              name: 'kiwisaverStatus',
              fieldsToCompare: ['kiwisaverStatus'],
            },
            {
              name: 'contributionOption',
              fieldsToCompare: ['contributionOption'],
            },
            {
              name: 'employeeContribution',
              fieldsToCompare: ['employeeContribution'],
            },
            {
              name: 'employerContribution',
              fieldsToCompare: ['employerContribution'],
            },
            {
              name: 'employerContributionThroughSalarySacrifice',
              fieldsToCompare: ['employerContributionThroughSalarySacrifice'],
            },
          ],
          fieldsToKeepWhenRequestEmployeeInformation: [
            'employerContribution',
            'employerContributionThroughSalarySacrifice',
          ],
        },
      }
    },
    requestInformationTemplate: {
      ['Basic Information']: {
        fields: [
          {
            name: 'basicInformationSection',
            fieldsToCompare: [],
          },
        ],
      },
      ['Contact Details']: {
        fields: [
          {
            name: 'contactDetailsSection',
            fieldsToCompare: [],
          },
        ],
      },
      ['Emergency Contact']: {
        fields: [
          {
            name: 'emergencyContactSection',
            fieldsToCompare: [],
          },
        ],
      },
      ['Bank Details']: {
        isPayrollRelated: true,
        fields: [
          {
            name: 'bankAccountName',
            fieldsToCompare: ['bankAccountName'],
          },
          {
            name: 'bankAccountNumber',
            fieldsToCompare: ['bankAccountNumber'],
          },
        ],
      },
      ['Tax Details']: {
        isPayrollRelated: true,
        fields: [
          {
            name: 'irdNumber',
            fieldsToCompare: ['irdNumber'],
          },
          {
            name: 'taxCode',
            fieldsToCompare: ['taxCode'],
          },
        ],
      },
      ['KiwiSaver']: {
        isPayrollRelated: true,
        fields: [
          {
            name: 'requestEmployeeInformationKiwisaverSection',
            fieldsToCompare: [],
          },
        ],
      },
    },
    transformToForm: (
      payrollEmployee: PayrollEmployee
    ): Partial<StaffMember> => {
      const transformedPayrollEmployee = payrollEmployee as XeroNzEmployee

      return {
        ...transformedPayrollEmployee,
        dob: transformedPayrollEmployee.dateOfBirth,
        datestampStarted: transformedPayrollEmployee.startDate,
        homeAddressStreet: transformedPayrollEmployee.addressLine1,
        homeAddressCity: transformedPayrollEmployee.city,
        homeAddressZipCode: transformedPayrollEmployee.postCode,
        homeAddressCountry: transformedPayrollEmployee.country,
      }
    },
    transformFromForm(
      partialStaffMember: Partial<StaffMemberNz>
    ): XeroNzEmployee {
      return {
        id: partialStaffMember.id!,
        firstName: partialStaffMember.firstName!,
        lastName: partialStaffMember.lastName,
        dateOfBirth: partialStaffMember.dob,
        startDate: partialStaffMember.datestampStarted,
        gender: partialStaffMember.gender,
        email: partialStaffMember.email,
        addressLine1: partialStaffMember.homeAddressStreet,
        city: partialStaffMember.homeAddressCity,
        postCode: partialStaffMember.homeAddressZipCode,
        country: partialStaffMember.homeAddressCountry,
        bankAccountName: partialStaffMember.bankAccountName,
        bankAccountNumber: partialStaffMember.bankAccountNumber,
        irdNumber: partialStaffMember.irdNumber,
        taxCode: partialStaffMember.taxCode,
        employerKiwisaverContributionTaxRate:
          partialStaffMember.employerKiwisaverContributionTaxRate,
        kiwisaverStatus: partialStaffMember.kiwisaverStatus,
        contributionOption: partialStaffMember.contributionOption,
        employeeContribution: partialStaffMember.employeeContribution,
        employerContribution: partialStaffMember.employerContribution,
        employerContributionThroughSalarySacrifice:
          partialStaffMember.employerContributionThroughSalarySacrifice,
      }
    },
    transformOnSave: (employee: PayrollEmployee) => {
      const nzEmployee = employee as XeroNzEmployee
      return {
        ...nzEmployee,
        dateOfBirth:
          nzEmployee.dateOfBirth != null
            ? getDateStringWithoutTimeZone(nzEmployee.dateOfBirth)
            : nzEmployee.dateOfBirth,
        startDate:
          nzEmployee.startDate != null
            ? getDateStringWithoutTimeZone(nzEmployee.startDate)
            : nzEmployee.startDate,
        irdNumber: nzEmployee.irdNumber?.replaceAll('-', ''),
        bankAccountNumber: nzEmployee.bankAccountNumber?.replaceAll('-', ''),
      }
    },
    transformOnGet: (employee: PayrollEmployee) => {
      const nzEmployee = employee as XeroNzEmployee
      return {
        ...nzEmployee,
        dateOfBirth:
          nzEmployee.dateOfBirth != null
            ? dayjs.utc(nzEmployee.dateOfBirth)
            : nzEmployee.dateOfBirth,
        startDate:
          nzEmployee.startDate != null
            ? dayjs.utc(nzEmployee.startDate)
            : nzEmployee.startDate,
        irdNumber: formatIRD(nzEmployee.irdNumber),
        bankAccountNumber: formatBankAccountNumber(
          nzEmployee.bankAccountNumber,
          Country.NewZealand
        ),
      }
    },
    validateOnCreate: (staffMember: Partial<StaffMember>) => {
      const missing: Array<{ label: string; fieldName: string }> = []

      if (staffMember.firstName == null || staffMember.firstName === '') {
        missing.push({ label: 'a first name', fieldName: 'firstName' })
      }

      if (staffMember.lastName == null || staffMember.lastName === '') {
        missing.push({ label: 'a last name', fieldName: 'lastName' })
      }

      if (staffMember.dob == null) {
        missing.push({ label: 'a birth date', fieldName: 'dob' })
      }

      if (
        staffMember.homeAddressStreet == undefined ||
        staffMember.homeAddressStreet === ''
      ) {
        missing.push({
          label: 'a street address',
          fieldName: 'homeAddressStreet',
        })
      }

      if (
        staffMember.homeAddressCity == undefined ||
        staffMember.homeAddressCity === ''
      ) {
        missing.push({ label: 'a city', fieldName: 'homeAddressCity' })
      }

      if (
        staffMember.homeAddressZipCode == undefined ||
        staffMember.homeAddressZipCode === ''
      ) {
        missing.push({ label: 'a postcode', fieldName: 'homeAddressZipCode' })
      }

      if (missing.length === 0) {
        return null
      }

      const fieldsList = stringArrayToHumanReadableList(
        missing.map((m) => m.label)
      )

      return {
        message: `${baseXeroEmployeeValidationMessage}${fieldsList}`,
        fieldNames: new Set(missing.map((m) => m.fieldName)),
      }
    },
  },
  [PayrollSystem.Custom]: {
    name: 'Custom Payroll',
    template: () => undefined,
  },
  [PayrollSystem.AcePayroll]: {
    name: 'Ace Payroll',
    template: () => undefined,
  },
  [PayrollSystem.AffinityPayroll]: {
    name: 'Affinity Payroll',
    template: () => undefined,
  },
  [PayrollSystem.CrystalPayroll]: {
    name: 'Crystal Payroll',
    template: () => undefined,
  },
  [PayrollSystem.DataPay]: {
    name: 'DataPay',
    template: () => undefined,
  },
  [PayrollSystem.EzyPay]: {
    name: 'EzyPay',
    template: () => undefined,
  },
  [PayrollSystem.Flexitime]: {
    name: 'Flexitime',
    template: () => undefined,
  },
  [PayrollSystem.IPayroll]: {
    name: 'IPayroll',
    template: () => undefined,
  },
  [PayrollSystem.MyobExo]: {
    name: 'MYOB EXO',
    template: () => undefined,
  },
  [PayrollSystem.MyobPayroll]: {
    name: 'MYOB Payroll',
    template: () => undefined,
  },
  [PayrollSystem.PayHero]: {
    name: 'PayHero',
    template: () => undefined,
  },
  [PayrollSystem.PaySauce]: {
    name: 'PaySauce',
    template: () => undefined,
  },
  [PayrollSystem.Smartly]: {
    name: 'Smartly',
    template: () => undefined,
  },
}

export const unavailabilityTypeNameMapping: Record<
  UnavailabilityRecordType,
  string
> = {
  [UnavailabilityRecordType.OneOff]: 'One-off Unavailability',
  [UnavailabilityRecordType.Weekly]: 'Weekly Unavailability',
}
